/**
 * The inbox's filter set, in the three places it has to survive.
 *
 * A filter set is read and written by the page (controls), the URL (so a view can
 * be pasted to someone else), and localStorage (both the last-used set and the
 * explicitly saved ones). Each of those wanted its own encoding, so this module
 * owns the one shape and the conversions in and out of it. Nothing here touches
 * React or the network — it is all pure, so the page and the tests agree.
 */

export const INBOX_SORT_OPTIONS = [
  { value: 'createdDate-desc', label: 'Newest First' },
  { value: 'createdDate-asc', label: 'Oldest First' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'title-asc', label: 'Title: A–Z' },
  { value: 'title-desc', label: 'Title: Z–A' },
  { value: 'featuredDate-desc', label: 'Recently Featured' },
  // From the search index (utils/inbox-score-sort.ts): to find items worth featuring.
  { value: 'deal-desc', label: 'Deal Score: Best First' },
  { value: 'quality-desc', label: 'Quality: Best First' },
  { value: 'photos-desc', label: 'Most Photos' },
  { value: 'photos-asc', label: 'Fewest Photos' },
  { value: 'id-desc', label: 'Listing ID: High to Low' },
  { value: 'id-asc', label: 'Listing ID: Low to High' },
] as const;

export type InboxSortOption = (typeof INBOX_SORT_OPTIONS)[number]['value'];

/** The same values as a plain tuple, for `z.enum` and for membership checks. */
export const INBOX_SORT_VALUES = INBOX_SORT_OPTIONS.map((o) => o.value) as unknown as readonly [
  InboxSortOption,
  ...InboxSortOption[],
];

export const DEFAULT_INBOX_SORT: InboxSortOption = 'createdDate-desc';

export function isInboxSortOption(value: unknown): value is InboxSortOption {
  return typeof value === 'string' && (INBOX_SORT_VALUES as readonly string[]).includes(value);
}

const SORT_LABELS = new Map<string, string>(INBOX_SORT_OPTIONS.map((o) => [o.value, o.label]));

export function inboxSortLabel(sortBy: string): string {
  return SORT_LABELS.get(sortBy) || sortBy;
}

/** Page sizes the reader can choose between. */
export const INBOX_PAGE_SIZES = [25, 50, 100, 200] as const;
export const DEFAULT_INBOX_LIMIT = 50;

/**
 * Sources the inbox offers.
 *
 * 'vendoo' is not a DATA_SOURCES row: those listings are dataSourceId 1, the same
 * as anything posted on SellWild, and carry a vendooListingId instead. It rides in
 * this control because to whoever is reading the inbox it is a source like any
 * other.
 */
export const INBOX_SOURCE_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: '1', label: 'Organic' },
  { value: 'vendoo', label: 'Vendoo' },
  { value: '27', label: 'Gateway Classic Cars' },
  { value: '28', label: 'MyEV' },
  { value: '29', label: 'Sella' },
  { value: '30', label: 'RescueGroups' },
  { value: '31', label: 'LotLinx' },
  { value: '33', label: 'BargainHunter' },
  { value: '35', label: 'SportServer' },
  { value: '40', label: 'CJ Dropshipping' },
] as const;

export const INBOX_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'featured', label: 'Featured' },
  { value: '1', label: 'Listed' },
  { value: '2', label: 'Chatting' },
  { value: '3', label: 'Purchased' },
  { value: '4', label: 'Sale Pending' },
] as const;

/**
 * How many listings per seller the inbox shows: 'auto' follows the ranking's
 * `maxPerSellerPerPage` knob, a number sets it for this view, 'all' turns the
 * limit off. Counted per category group, by SUPPLY_LISTINGS.sellerRank.
 */
export type InboxPerSeller = 'auto' | 'all' | number;

export const INBOX_PER_SELLER_OPTIONS: ReadonlyArray<{ value: InboxPerSeller; label: string }> = [
  { value: 'auto', label: 'Ranking default' },
  { value: 1, label: '1 per seller' },
  { value: 2, label: '2 per seller' },
  { value: 3, label: '3 per seller' },
  { value: 5, label: '5 per seller' },
  { value: 10, label: '10 per seller' },
  { value: 25, label: '25 per seller' },
  { value: 'all', label: 'All listings' },
];

const PER_SELLER_NUMBERS: readonly number[] = INBOX_PER_SELLER_OPTIONS.flatMap(o => (typeof o.value === 'number' ? [o.value] : []));

/** A per-seller value from anywhere (URL, storage): unknown values fall back to 'auto'. */
export function parseInboxPerSeller(raw: unknown): InboxPerSeller {
  if (raw === 'all') return 'all';
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' && /^\d+$/.test(raw) ? Number(raw) : NaN;
  return PER_SELLER_NUMBERS.includes(n) ? n : 'auto';
}

export interface InboxFilters {
  selectedGroup: number | 'all';
  selectedCategory: number | 'all';
  selectedStatus: number | 'all' | 'featured';
  selectedDataSource: number | 'all' | 'vendoo';
  selectedPartner: number | 'all';
  sortBy: InboxSortOption;
  searchTerm: string;
  selectedUserId: string;
  showSold: boolean;
  showShippable: boolean;
  /** Deals Only / Quality Only: score floors from utils/deal-quality-filter.ts. */
  showDeals: boolean;
  showQuality: boolean;
  /** Listings kept out of rows and browse (utils/not-recommended.ts), to review what Jev found. */
  showNsfw: boolean;
  showArchived: boolean;
  /**
   * Listings per seller. By default ('auto') the inbox hides a seller's
   * listings past the ranking's per-seller limit, so its top is a mix of
   * sellers rather than one seller's bulk upload.
   */
  perSeller: InboxPerSeller;
  limit: number;
  page: number;
}

export const DEFAULT_INBOX_FILTERS: InboxFilters = {
  selectedGroup: 'all',
  selectedCategory: 'all',
  selectedStatus: 'all',
  selectedDataSource: 'all',
  selectedPartner: 'all',
  sortBy: DEFAULT_INBOX_SORT,
  searchTerm: '',
  selectedUserId: '',
  showSold: false,
  showShippable: false,
  showDeals: false,
  showQuality: false,
  showNsfw: false,
  showArchived: false,
  perSeller: 'auto',
  limit: DEFAULT_INBOX_LIMIT,
  page: 1,
};

/**
 * The filters a saved search remembers — everything but which page you were on.
 * Recalling a search you saved on page 7 and landing on page 7 of a list you have
 * not looked at yet is never what was meant.
 */
export type InboxSavedFilters = Omit<InboxFilters, 'page'>;

export function withoutPage(filters: InboxFilters): InboxSavedFilters {
  const { page: _page, ...rest } = filters;
  return rest;
}

/** Field-by-field, so two sets built from different sources still compare equal. */
export function sameInboxFilters(a: InboxSavedFilters, b: InboxSavedFilters): boolean {
  return (
    a.selectedGroup === b.selectedGroup &&
    a.selectedCategory === b.selectedCategory &&
    a.selectedStatus === b.selectedStatus &&
    a.selectedDataSource === b.selectedDataSource &&
    a.selectedPartner === b.selectedPartner &&
    a.sortBy === b.sortBy &&
    a.searchTerm.trim() === b.searchTerm.trim() &&
    a.selectedUserId === b.selectedUserId &&
    a.showSold === b.showSold &&
    a.showShippable === b.showShippable &&
    a.showDeals === b.showDeals &&
    a.showQuality === b.showQuality &&
    a.showNsfw === b.showNsfw &&
    a.showArchived === b.showArchived &&
    a.perSeller === b.perSeller &&
    a.limit === b.limit
  );
}

export function isDefaultInboxFilters(filters: InboxSavedFilters): boolean {
  return sameInboxFilters(filters, withoutPage(DEFAULT_INBOX_FILTERS));
}

// ---------------------------------------------------------------------------
// URL query string
// ---------------------------------------------------------------------------

/**
 * `userId` keeps its long name: /dash/inbox?userId=99 is the link the sellers page
 * has always used, and shortening it here would quietly break it.
 */
const QUERY_KEYS = {
  group: 'group',
  category: 'category',
  status: 'status',
  source: 'source',
  partner: 'partner',
  sort: 'sort',
  search: 'q',
  userId: 'userId',
  sold: 'sold',
  shippable: 'shippable',
  deals: 'deals',
  quality: 'quality',
  nsfw: 'nsfw',
  archived: 'archived',
  perSeller: 'perSeller',
  /** Read only: the boolean this replaced. `allPerSeller=true` still means 'all'. */
  allPerSeller: 'allPerSeller',
  limit: 'limit',
  page: 'page',
} as const;

export const INBOX_QUERY_KEYS: readonly string[] = Object.values(QUERY_KEYS);

/**
 * Only what differs from the defaults, so an unfiltered inbox has a clean URL and
 * the address bar stays readable as filters are added.
 */
export function inboxFiltersToQuery(filters: InboxFilters): Record<string, string> {
  const query: Record<string, string> = {};

  if (filters.selectedGroup !== 'all') query[QUERY_KEYS.group] = String(filters.selectedGroup);
  if (filters.selectedCategory !== 'all') query[QUERY_KEYS.category] = String(filters.selectedCategory);
  if (filters.selectedStatus !== 'all') query[QUERY_KEYS.status] = String(filters.selectedStatus);
  if (filters.selectedDataSource !== 'all') query[QUERY_KEYS.source] = String(filters.selectedDataSource);
  if (filters.selectedPartner !== 'all') query[QUERY_KEYS.partner] = String(filters.selectedPartner);
  if (filters.sortBy !== DEFAULT_INBOX_SORT) query[QUERY_KEYS.sort] = filters.sortBy;
  if (filters.searchTerm) query[QUERY_KEYS.search] = filters.searchTerm;
  if (filters.selectedUserId) query[QUERY_KEYS.userId] = filters.selectedUserId;
  if (filters.showSold) query[QUERY_KEYS.sold] = 'true';
  if (filters.showShippable) query[QUERY_KEYS.shippable] = 'true';
  if (filters.showDeals) query[QUERY_KEYS.deals] = 'true';
  if (filters.showQuality) query[QUERY_KEYS.quality] = 'true';
  if (filters.showNsfw) query[QUERY_KEYS.nsfw] = 'true';
  if (filters.showArchived) query[QUERY_KEYS.archived] = 'true';
  if (filters.perSeller !== 'auto') query[QUERY_KEYS.perSeller] = String(filters.perSeller);
  if (filters.limit !== DEFAULT_INBOX_LIMIT) query[QUERY_KEYS.limit] = String(filters.limit);
  if (filters.page > 1) query[QUERY_KEYS.page] = String(filters.page);

  return query;
}

type QueryValue = string | string[] | undefined;

function readOne(value: QueryValue): string | undefined {
  if (Array.isArray(value)) return value[0];
  return value;
}

function readChoice(value: QueryValue, allowed: readonly string[]): number | 'all' | undefined {
  const raw = readOne(value);
  if (raw === undefined || raw === '') return undefined;
  if (raw === 'all') return 'all';
  if (!allowed.includes(raw)) return undefined;
  // Every allowed value but 'all' (handled above) and the named ones the callers
  // read first ('vendoo', 'featured') is a numeric string, so this is a number.
  return Number(raw);
}

function readNumber(value: QueryValue): number | 'all' | undefined {
  const raw = readOne(value);
  if (raw === undefined || raw === '') return undefined;
  if (raw === 'all') return 'all';
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

function readBool(value: QueryValue): boolean | undefined {
  const raw = readOne(value);
  if (raw === undefined) return undefined;
  return raw === 'true' || raw === '1';
}

/** True once the URL carries anything the inbox recognises. */
export function hasInboxQuery(query: Record<string, QueryValue>): boolean {
  return INBOX_QUERY_KEYS.some((key) => {
    const value = readOne(query[key]);
    return value !== undefined && value !== '';
  });
}

/**
 * Whatever the URL states, ignoring the rest. A hand-edited or stale link is
 * common enough that an unreadable value has to fall through to the default
 * rather than blank the page.
 */
export function inboxFiltersFromQuery(query: Record<string, QueryValue>): Partial<InboxFilters> {
  const filters: Partial<InboxFilters> = {};

  const group = readNumber(query[QUERY_KEYS.group]);
  if (group !== undefined) filters.selectedGroup = group;

  const category = readNumber(query[QUERY_KEYS.category]);
  if (category !== undefined) filters.selectedCategory = category;

  const status = readOne(query[QUERY_KEYS.status]);
  if (status === 'featured') {
    filters.selectedStatus = 'featured';
  } else {
    const numeric = readChoice(
      query[QUERY_KEYS.status],
      INBOX_STATUS_OPTIONS.map((o) => o.value)
    );
    if (numeric !== undefined) filters.selectedStatus = numeric;
  }

  const source = readOne(query[QUERY_KEYS.source]);
  if (source === 'vendoo') {
    filters.selectedDataSource = 'vendoo';
  } else {
    const numeric = readChoice(
      query[QUERY_KEYS.source],
      INBOX_SOURCE_OPTIONS.map((o) => o.value)
    );
    if (numeric !== undefined) filters.selectedDataSource = numeric;
  }

  const partner = readNumber(query[QUERY_KEYS.partner]);
  if (partner !== undefined) filters.selectedPartner = partner;

  const sort = readOne(query[QUERY_KEYS.sort]);
  if (isInboxSortOption(sort)) filters.sortBy = sort;

  const search = readOne(query[QUERY_KEYS.search]);
  if (search !== undefined) filters.searchTerm = search;

  const userId = readOne(query[QUERY_KEYS.userId]);
  if (userId !== undefined) filters.selectedUserId = userId;

  const sold = readBool(query[QUERY_KEYS.sold]);
  if (sold !== undefined) filters.showSold = sold;

  const shippable = readBool(query[QUERY_KEYS.shippable]);
  if (shippable !== undefined) filters.showShippable = shippable;

  const deals = readBool(query[QUERY_KEYS.deals]);
  if (deals !== undefined) filters.showDeals = deals;

  const quality = readBool(query[QUERY_KEYS.quality]);
  if (quality !== undefined) filters.showQuality = quality;

  const nsfw = readBool(query[QUERY_KEYS.nsfw]);
  if (nsfw !== undefined) filters.showNsfw = nsfw;

  const archived = readBool(query[QUERY_KEYS.archived]);
  if (archived !== undefined) filters.showArchived = archived;

  const perSeller = readOne(query[QUERY_KEYS.perSeller]);
  if (perSeller !== undefined && perSeller !== '') {
    filters.perSeller = parseInboxPerSeller(perSeller);
  } else if (readBool(query[QUERY_KEYS.allPerSeller])) {
    filters.perSeller = 'all';
  }

  const limit = readOne(query[QUERY_KEYS.limit]);
  const limitNum = limit ? Number(limit) : NaN;
  if ((INBOX_PAGE_SIZES as readonly number[]).includes(limitNum)) filters.limit = limitNum;

  const page = readOne(query[QUERY_KEYS.page]);
  const pageNum = page ? Number(page) : NaN;
  if (Number.isFinite(pageNum) && pageNum >= 1) filters.page = Math.floor(pageNum);

  return filters;
}

// ---------------------------------------------------------------------------
// Reading a filter set at a glance
// ---------------------------------------------------------------------------

/** Tones a summary pill can take. Kept as names so the component owns the CSS. */
export type InboxPillTone = 'search' | 'neutral' | 'featured' | 'sold' | 'shippable' | 'deals' | 'quality' | 'nsfw' | 'archived';

export interface InboxFilterPill {
  key: string;
  /** Small-caps prefix — SEARCH, SOURCE. Omitted for pills that are their own label. */
  label?: string;
  value: string;
  tone: InboxPillTone;
}

export interface InboxLabelLookups {
  categoryLabel?: (id: number) => string | undefined;
  partnerLabel?: (membershipType: number) => string | undefined;
}

function optionLabel(options: readonly { value: string; label: string }[], value: string): string {
  return options.find((o) => o.value === value)?.label || value;
}

/**
 * The filter set as a short row of pills — the same read-at-a-glance shape the
 * header's recent searches use, so a saved search can be recognised without
 * applying it first.
 *
 * Sort is deliberately included: two saved searches that differ only by sort order
 * would otherwise look identical.
 */
export function describeInboxFilters(
  filters: InboxSavedFilters,
  lookups: InboxLabelLookups = {}
): InboxFilterPill[] {
  const pills: InboxFilterPill[] = [];

  if (filters.searchTerm.trim()) {
    pills.push({ key: 'search', label: 'Search', value: filters.searchTerm.trim(), tone: 'search' });
  }

  if (filters.selectedCategory !== 'all') {
    pills.push({
      key: 'category',
      label: 'Category',
      value: lookups.categoryLabel?.(filters.selectedCategory) || `#${filters.selectedCategory}`,
      tone: 'neutral',
    });
  } else if (filters.selectedGroup !== 'all') {
    pills.push({
      key: 'group',
      label: 'Group',
      value: lookups.categoryLabel?.(filters.selectedGroup) || `#${filters.selectedGroup}`,
      tone: 'neutral',
    });
  }

  if (filters.selectedDataSource !== 'all') {
    pills.push({
      key: 'source',
      label: 'Source',
      value: optionLabel(INBOX_SOURCE_OPTIONS, String(filters.selectedDataSource)),
      tone: 'neutral',
    });
  }

  if (filters.selectedStatus === 'featured') {
    pills.push({ key: 'status', value: 'Featured', tone: 'featured' });
  } else if (filters.selectedStatus !== 'all') {
    pills.push({
      key: 'status',
      label: 'Status',
      value: optionLabel(INBOX_STATUS_OPTIONS, String(filters.selectedStatus)),
      tone: 'neutral',
    });
  }

  if (filters.selectedPartner !== 'all') {
    pills.push({
      key: 'partner',
      label: 'Partner',
      value: lookups.partnerLabel?.(filters.selectedPartner) || `#${filters.selectedPartner}`,
      tone: 'neutral',
    });
  }

  if (filters.selectedUserId) {
    pills.push({ key: 'seller', label: 'Seller', value: `#${filters.selectedUserId}`, tone: 'neutral' });
  }

  if (filters.showSold) pills.push({ key: 'sold', value: 'Sold', tone: 'sold' });
  if (filters.showShippable) pills.push({ key: 'shippable', value: 'Shippable', tone: 'shippable' });
  if (filters.showDeals) pills.push({ key: 'deals', value: 'Deals', tone: 'deals' });
  if (filters.showQuality) pills.push({ key: 'quality', value: 'Quality', tone: 'quality' });
  if (filters.showNsfw) pills.push({ key: 'nsfw', value: 'NSFW', tone: 'nsfw' });
  if (filters.showArchived) pills.push({ key: 'archived', value: 'Archived', tone: 'archived' });
  if (filters.perSeller !== 'auto') {
    pills.push({
      key: 'perSeller',
      label: 'Per seller',
      value: filters.perSeller === 'all' ? 'All' : String(filters.perSeller),
      tone: 'neutral',
    });
  }

  if (filters.sortBy !== DEFAULT_INBOX_SORT) {
    pills.push({ key: 'sort', label: 'Sort', value: inboxSortLabel(filters.sortBy), tone: 'neutral' });
  }

  if (filters.limit !== DEFAULT_INBOX_LIMIT) {
    pills.push({ key: 'limit', label: 'Per page', value: String(filters.limit), tone: 'neutral' });
  }

  if (pills.length === 0) {
    pills.push({ key: 'all', value: 'All listings', tone: 'neutral' });
  }

  return pills;
}

// ---------------------------------------------------------------------------
// Saved searches (localStorage)
// ---------------------------------------------------------------------------

export const SAVED_SEARCHES_KEY = 'dash-inbox-saved-searches';

/** Nothing about a saved search is worth a server round-trip, so it stays local. */
export interface SavedInboxSearch {
  id: string;
  filters: InboxSavedFilters;
  savedAt: number;
}

export const MAX_SAVED_SEARCHES = 12;

/**
 * Merge over the defaults rather than trusting the stored object: a search saved
 * before a filter existed has no key for it, and a hand-edited entry can hold
 * anything at all.
 */
function coerceSavedFilters(raw: unknown): InboxSavedFilters | null {
  if (!raw || typeof raw !== 'object') return null;
  const stored = raw as Record<string, unknown>;
  const base = withoutPage(DEFAULT_INBOX_FILTERS);

  const choice = <T,>(value: unknown, fallback: T): number | T =>
    value === 'all' || typeof value === 'number' ? (value as number | T) : fallback;

  return {
    ...base,
    selectedGroup: choice(stored.selectedGroup, base.selectedGroup),
    selectedCategory: choice(stored.selectedCategory, base.selectedCategory),
    selectedStatus:
      stored.selectedStatus === 'featured'
        ? 'featured'
        : choice(stored.selectedStatus, base.selectedStatus),
    selectedDataSource:
      stored.selectedDataSource === 'vendoo'
        ? 'vendoo'
        : choice(stored.selectedDataSource, base.selectedDataSource),
    selectedPartner: choice(stored.selectedPartner, base.selectedPartner),
    sortBy: isInboxSortOption(stored.sortBy) ? stored.sortBy : base.sortBy,
    searchTerm: typeof stored.searchTerm === 'string' ? stored.searchTerm : base.searchTerm,
    selectedUserId: typeof stored.selectedUserId === 'string' ? stored.selectedUserId : base.selectedUserId,
    showSold: stored.showSold === true,
    showShippable: stored.showShippable === true,
    showDeals: stored.showDeals === true,
    showQuality: stored.showQuality === true,
    showNsfw: stored.showNsfw === true,
    showArchived: stored.showArchived === true,
    perSeller:
      stored.perSeller !== undefined
        ? parseInboxPerSeller(stored.perSeller)
        : (stored as { showAllPerSeller?: unknown }).showAllPerSeller === true ? 'all' : 'auto',
    limit:
      typeof stored.limit === 'number' && (INBOX_PAGE_SIZES as readonly number[]).includes(stored.limit)
        ? stored.limit
        : base.limit,
  };
}

export function parseSavedSearches(raw: string | null): SavedInboxSearch[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((entry, index): SavedInboxSearch | null => {
      if (!entry || typeof entry !== 'object') return null;
      const record = entry as Record<string, unknown>;
      const filters = coerceSavedFilters(record.filters);
      if (!filters) return null;
      return {
        id: typeof record.id === 'string' && record.id ? record.id : `saved-${index}`,
        filters,
        savedAt: typeof record.savedAt === 'number' ? record.savedAt : 0,
      };
    })
    .filter((entry): entry is SavedInboxSearch => entry !== null)
    .slice(0, MAX_SAVED_SEARCHES);
}

/**
 * Newest first, deduped: re-saving a set you already have moves nothing and adds
 * nothing, so mashing the button cannot fill the row with copies.
 */
export function addSavedSearch(
  existing: SavedInboxSearch[],
  filters: InboxSavedFilters,
  id: string,
  savedAt: number
): SavedInboxSearch[] {
  if (existing.some((entry) => sameInboxFilters(entry.filters, filters))) return existing;
  return [{ id, filters, savedAt }, ...existing].slice(0, MAX_SAVED_SEARCHES);
}

export function removeSavedSearch(existing: SavedInboxSearch[], id: string): SavedInboxSearch[] {
  return existing.filter((entry) => entry.id !== id);
}
